// Save-time send guardrail for Group Smart Actions.
//
// Shared by BOTH group Smart Action editors so the two can never drift:
//   - TabSmartAction (legacy editor, users without SMART_ACTIONS_ACTION_RULES)
//   - EventActionRulesSurface (action-rules editor, permissioned users)
//
// Group only. Event and Event Type Smart Actions anchor day 0 to the party
// start date rather than a contact's join date, so neither the wording nor the
// reasoning transfers; their callers must not use this.
//
// The check evaluates on save rather than on field entry, because a consultant
// can sit on the form long enough for a time that was comfortably ahead to fall
// into the past.

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import moment from 'moment-timezone';

// Lead time added to "now" before rounding, so the suggested Time of Day is far
// enough ahead to actually be usable by the time the consultant re-saves.
const SUGGESTION_LEAD_MINUTES = 10;
const SUGGESTION_ROUND_TO_MINUTES = 15;

// Falls back to the browser's zone only when the user record carries none. The
// backend Drip materializer reads the same user-level timezone, and groups have
// no timezone of their own.
const resolveTimeZone = (timeZone) => timeZone || moment.tz.guess();

// Parses a 'HH:mm' Time of Day into hour/minute numbers, or null when unset or
// malformed. Yup already requires this field for Drip; the null path only
// guards the guardrail itself from throwing.
const parseTimeOfDay = (timeOfDay) => {
  const [rawHour, rawMinute] = String(timeOfDay || '').split(':');
  const hour = parseInt(rawHour, 10);
  const minute = parseInt(rawMinute, 10);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }

  return { hour, minute };
};

// now + SUGGESTION_LEAD_MINUTES rounded up to the next clean quarter hour, so
// the suggested time reads like something a person would pick rather than a
// computed one. Already-clean marks are kept rather than pushed a step further.
const getSuggestedSendTime = (now) => {
  const suggestion = now.clone().add(SUGGESTION_LEAD_MINUTES, 'minutes').seconds(0).milliseconds(0);
  const remainder = suggestion.minutes() % SUGGESTION_ROUND_TO_MINUTES;

  return remainder === 0
    ? suggestion
    : suggestion.add(SUGGESTION_ROUND_TO_MINUTES - remainder, 'minutes');
};

// Soft warning: a Drip day-0 action whose Time of Day has already passed today
// reaches nobody currently in the group. Legitimate for a consultant who wants
// that time going forward for people who join tomorrow, so callers confirm
// rather than block.
//
// Returns the warning as an ARRAY OF SENTENCES, or null when there is nothing
// to warn about. Callers render one sentence per line: this is a lot of text to
// absorb at save time, and a single run-on paragraph does not get read.
export const getPastDayZeroWarningLines = ({ isDrip, values, timeZone }) => {
  if (!isDrip || parseInt(values?.dayOffset, 10) !== 0) {
    return null;
  }

  const parsedTime = parseTimeOfDay(values?.timeOfDay);

  if (!parsedTime) {
    return null;
  }

  const now = moment.tz(resolveTimeZone(timeZone));
  const chosenSendTime = now.clone().set({
    hours: parsedTime.hour,
    minutes: parsedTime.minute,
    seconds: 0,
    milliseconds: 0,
  });

  if (!chosenSendTime.isBefore(now)) {
    return null;
  }

  const suggestedSendTime = getSuggestedSendTime(now);
  // Past midnight there is no time today that reaches current members, so the
  // suggestion is dropped rather than naming a time on tomorrow's clock.
  const suggestionSentence = suggestedSendTime.isSame(now, 'day')
    ? [{
      text: `To reach them today, change Time of Day to ${suggestedSendTime.format('h:mm A')} or later.`,
      emphasis: true,
    }]
    : [];

  // emphasis marks the consequence and the fix — the part a consultant must
  // actually act on. Carried as a flag rather than inferred from position,
  // because the suggestion line drops out past midnight and positional
  // emphasis would then land on the wrong sentence.
  return [
    { text: `The ${chosenSendTime.format('h:mm A')} send time has already passed.`, emphasis: false },
    { text: 'Contacts already in this group will not receive this message.', emphasis: true },
    ...suggestionSentence,
  ];
};

// Renders the warning sentences as the ModalDialogMUI6 `content` node, one per
// line with a gap between. Shared so both editors present the warning
// identically. Centered to sit with the modal's centered title; the emphasised
// sentences carry the consequence and the fix, so they read first.
export const renderSendGuardContent = (lines) => (
  <Box>
    {(lines || []).map(({ text, emphasis }) => (
      <Typography
        key={text}
        variant="body1"
        sx={{
          mb: 1.5,
          textAlign: 'center',
          fontWeight: emphasis ? 600 : 400,
          color: emphasis ? 'text.primary' : 'text.secondary',
          '&:last-of-type': { mb: 0 },
        }}
      >
        {text}
      </Typography>
    ))}
  </Box>
);

// CON-1801 — recognises the backend's "currently sending" refusal for a Group
// Smart Action: a 409 whose body carries sendingProgress. updateSmartAction,
// the action-rule seed and createActionRule all return the same body, so one
// reader serves every path. Returns the progress pair for the "Action
// Currently Sending" dialog, or null for any other error.
export const getSendingConflict = (error) => {
  const { status, data } = error?.response || {};
  const { sendingProgress } = data || {};

  if (status !== 409 || !sendingProgress) {
    return null;
  }

  return {
    totalContacts: sendingProgress.totalContacts || 0,
    contactsSent: sendingProgress.contactsSent || 0,
  };
};
